/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DestroyRef, OnDestroy, OnInit} from '@angular/core';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {NgBusyModule} from 'ng-busy';
import {Observable, Subscription, take, tap} from 'rxjs';
import {NavigationStart, Router, RouterModule, RouterOutlet} from '@angular/router';
import {LookupService} from './common/service/lookup.service';
import {AuthenticatorService} from './common/service/authenticator.service';
import {HttpClient} from '@angular/common/http';
import {isDefined} from './common/service/data-util';
import {AppConfigService} from './common/service/app-config.service';
import {UserPreferencesService} from './common/service/user-preferences.service';
import {SessionManagerService} from './common/service/session-manager.service';
import {PermissionsService} from './common/service/permissions.service';

import {NotificationAlertsComponent} from './common/notification-alerts/notification-alerts.component';
import {ArcgisGeometryOverride} from './design/esri-nlr/services/geometry-custom.override';
import {ArcgisNavigationOverride} from './design/esri-nlr/services/mouse-events-custom.override';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {EsriModuleService} from './design/esri-nlr/services/esri-module.service';
import {ArcGisAuthService} from './design/services/arc-gis-auth.service';
import {MapToolsService} from './design/services/map-tools.service';
import {filter, map, switchMap} from 'rxjs/operators';
import {AnalyticsService} from './common/service/analytics.service';
import {WidgetWindow} from './design/window-widgets/window-widgets.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'nlr-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterModule, RouterOutlet, NgBusyModule, NotificationAlertsComponent, DragDropModule]
})
export class AppComponent implements OnInit, OnDestroy {
    busy: Subscription[] = [];

    /**
     * Note! geometryCustomOverride and spacialQueryOverride are overriding default esri settings.
     * They must be initialised and injected here but not used anywhere else.
     */
    constructor(
        private eventBus: NgEventBus,
        private http: HttpClient,
        private lookupService: LookupService,
        private authenticatorService: AuthenticatorService,
        private permissionsService: PermissionsService,
        private appConfigService: AppConfigService,
        private userPreferencesService: UserPreferencesService,
        private sessionManagerService: SessionManagerService,
        private geometryCustomOverride: ArcgisGeometryOverride,
        private spacialQueryOverride: ArcgisNavigationOverride,
        private esriModuleService: EsriModuleService,
        private arcGisAuthService: ArcGisAuthService,
        private mapToolsService: MapToolsService,
        private analyticsService: AnalyticsService,
        private router: Router,
        private destroyRef: DestroyRef
    ) {}

    isReferenceDataLoaded() {
        return this.lookupService.lookupsLoaded && this.userPreferencesService.userPreferencesLoaded && this.esriModuleService.modulesLoaded();
    }

    ngOnInit(): void {
        console.log('Initialise app...');
        this.eventBus
            .on('nlr:busy')
            .pipe(
                /**
                 * PAU-1296 (and others) - When `nlr:busy` is published with a subscription to save the plan, the code paths
                 * end up updating parts of the model that result in subsequent calls to other services in other components, such as
                 * findAllRoadsByLocality, and dependantPlans. As these calls originate from unrelated components to the one that
                 * initiated the plan save, and are invoked _after_ plan save is complete, the `busy` array in this component goes
                 * from 1 item (the plan save) to 0 items, then back to 1 (or more). The result on the end user is they see the
                 * "processing" loading UI disappear, and then reappear very quickly, resulting in a poor experience.
                 * .
                 * This switchMap introduces a delay of a few milliseconds _after_ each subscription received by `nlr:busy` completes
                 * before the "processing" loading UI disappears. This gives unrelated components enough time to react to the model change
                 * and publish new subscriptions to `nlr:busy` without the user experiencing the flash in the UI.
                 */
                switchMap((meta: MetaData<Subscription>) => {
                    this.busy.push(meta.data);
                    const obs = new Observable(subscriber => {
                        meta.data.add(() => {
                            setTimeout(() => {
                                subscriber.next(null);
                                subscriber.complete();
                            }, 200);
                        });
                    });
                    this.busy.push(obs.pipe(take(1)).subscribe());
                    return obs;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe((event: NavigationStart) => {
            this.analyticsService.pageNavigation('config', {page_path: event.url});
        });

        const jwtToken = window.localStorage['jwtToken'];
        const codeIndex = window.location.search.indexOf('code=');
        const nextParamIndex = window.location.search.indexOf('&');
        let code = undefined;
        if (codeIndex > 0) {
            if (nextParamIndex > 0 && nextParamIndex > codeIndex + 5) {
                code = window.location.search.substr(codeIndex + 5, nextParamIndex);
            } else {
                code = window.location.search.substr(codeIndex + 5);
            }

            const subs = this.appConfigService
                .loadSettings()
                .pipe(switchMap(() => this.http.get(this.appConfigService.settings.apiGatewayUrl + '/auth/loginPF', {params: {code: code}})))
                .subscribe({
                    next: (data: any) => {
                        console.log('Logged in');
                        if (isDefined(data.token)) {
                            if (window.localStorage['jwtToken']) {
                                window.localStorage.removeItem('jwtToken');
                            }
                            //$http.defaults.headers.common['X-AUTH-TOKEN'] = data.token;
                            window.localStorage['jwtToken'] = data.token;
                            window.location.href = '/#/home';
                        } else {
                            window.localStorage.removeItem('jwtToken');
                            this.authenticatorService.redirectToPF();
                        }
                    },
                    error: () => {
                        console.error('Not logged in... Redirecting to AUTH');
                        this.authenticatorService.redirectToPF();
                    },
                    complete: () => {}
                });
            this.eventBus.cast('nlr:busy', subs);
        } else if (jwtToken !== undefined) {
            const windowContext = (window as WidgetWindow).nlrpContext;

            if (windowContext) {
                // Copy service state to avoid rerunning time-consuming requests
                Object.assign(this.arcGisAuthService, windowContext.arcGisAuthService);
                Object.assign(this.lookupService, windowContext.lookupService);
                Object.assign(this.permissionsService, windowContext.permissionsService);
                Object.assign(this.userPreferencesService, windowContext.userPreferencesService);
            }

            //If we are not logged in, it will kick us out.
            const subs = this.appConfigService
                .loadSettings()
                .pipe(
                    switchMap(() => this.authenticatorService.isLoggedIn()),
                    map(() => this.analyticsService.gtag('set', {user_id: this.authenticatorService.getUsername()})),
                    switchMap(() => this.lookupService.loadAllLookups()),
                    switchMap(() => this.permissionsService.loadPermissions()),
                    switchMap(() => this.appConfigService.getApplicationConfiguration()),
                    switchMap(() => this.arcGisAuthService.authenticateArcGisUser()),
                    switchMap(() => this.userPreferencesService.loadUserPreferences()),
                    switchMap(() => this.sessionManagerService.init()),
                    switchMap(() => this.esriModuleService.init()),
                    tap(() => this.mapToolsService.init())
                )
                .subscribe({
                    next: () => {
                        console.info(`NLR-P Release ${this.appConfigService.getReleaseVersion()}`);
                    },
                    error: error => {
                        console.error('Not logged in... Redirecting to AUTH', error);
                        this.authenticatorService.redirectToPF();
                    },
                    complete: () => {}
                });
            this.eventBus.cast('nlr:busy', subs);
        } else {
            // Redirect to authentication
            const subs = this.appConfigService.loadSettings().subscribe(() => {
                this.authenticatorService.redirectToPF();
            });
            this.eventBus.cast('nlr:busy', subs);
        }
    }
    ngOnDestroy(): void {
        this.sessionManagerService.clear();
    }
}
